import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { ReactComponent as TickSelectIcon } from '../../icons/tick-select.svg';
import Lightbox from 'yet-another-react-lightbox';
import FileLink from './FileLink';
import 'yet-another-react-lightbox/styles.css';
import './image-card.scss';

export interface ImageCardField {
  label: string;
  value: string;
}

export interface ImageCardLink {
  label: string;
  url: string;
}

export interface ImageCardProps {
  id: string;
  title: string;
  blurb?: string;
  imageUrl?: string;
  additionalImages?: string[];
  fields?: ImageCardField[];
  links?: ImageCardLink[];
  selected?: boolean;
  onSelect?: () => void;
  readOnly?: boolean;
  /** Accent colour for links */
  accentColor?: string;
}

const ImageCard: React.FC<ImageCardProps> = ({
  id,
  title,
  blurb,
  imageUrl,
  additionalImages,
  fields,
  links,
  selected = false,
  onSelect,
  readOnly = false,
  accentColor,
}) => {
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const displayImage = activeImage || imageUrl;

  const allImages = useMemo(() => {
    const imgs: string[] = [];
    if (imageUrl) imgs.push(imageUrl);
    if (additionalImages) {
      for (const url of additionalImages) {
        if (url && !imgs.includes(url)) imgs.push(url);
      }
    }
    return imgs;
  }, [imageUrl, additionalImages]);

  const handleImageClick = useCallback((e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    setActiveImage(url);
  }, []);

  const openLightbox = useCallback(
    (e: React.MouseEvent, startIndex: number) => {
      e.stopPropagation();
      setLightboxIndex(startIndex);
    },
    [],
  );

  const hasImages = !!imageUrl;
  const hasAdditional = additionalImages && additionalImages.length > 0;

  return (
    <div
      className={`image-card${selected ? ' selected' : ''}${readOnly ? ' read-only' : ''}`}
      onClick={onSelect}
      role='button'
      tabIndex={0}
    >
      <div className='image-heavy-content'>
        {hasImages && (
          <div className='images-wrapper'>
            <div className={`card-tick${selected ? ' selected' : ''}`}>
              <TickSelectIcon />
            </div>
            <div
              className='main-image'
              style={{ backgroundImage: `url(${displayImage})` }}
              onClick={(e) =>
                openLightbox(e, Math.max(0, allImages.indexOf(displayImage!)))
              }
            >
              <div className='internal-image-overlay'></div>
            </div>
            {hasAdditional && (
              <div className='additional-images'>
                <div
                  className={`additional-image${!activeImage || activeImage === imageUrl ? ' active' : ''}`}
                  style={{ backgroundImage: `url(${imageUrl})` }}
                  onClick={(e) => handleImageClick(e, imageUrl!)}
                />
                {additionalImages!.slice(0, 4).map((url, idx) => (
                  <div
                    key={idx}
                    className={`additional-image${activeImage === url ? ' active' : ''}`}
                    style={{ backgroundImage: `url(${url})` }}
                    onClick={(e) => handleImageClick(e, url)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <div className='card-text'>
          <b>{title}</b>
          {blurb && (
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
              {blurb}
            </ReactMarkdown>
          )}
          {fields && fields.length > 0 && (
            <div className='card-fields'>
              {fields.map((field, idx) => (
                <div key={idx} className='card-field'>
                  <span className='field-label'>{field.label}</span>
                  <span className='field-value'>{field.value}</span>
                </div>
              ))}
            </div>
          )}
          {links && links.length > 0 && (
            <div className='card-links'>
              {links.map((link, idx) => (
                <FileLink
                  key={idx}
                  url={link.url}
                  onClick={(e) => e.stopPropagation()}
                  style={accentColor ? { color: accentColor } : undefined}
                >
                  {link.label}
                </FileLink>
              ))}
            </div>
          )}
        </div>
      </div>
      {allImages.length > 0 && (
        <Lightbox
          open={lightboxIndex >= 0}
          close={() => setLightboxIndex(-1)}
          index={lightboxIndex}
          slides={allImages.map((url) => ({ src: url }))}
        />
      )}
    </div>
  );
};

export default ImageCard;
